/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { EMPTY_ROLE } from '../const';
import { PersonDescription, SourcesType } from './content_v1';
import { baseQueryWithReauth } from './reauth';
import { SdnPermissionType } from './sdn';
import { AccountDiskWithOwner, AccountListElement, AuthResponse, Role, userApiV1 } from './users_v1';

export type Language = 'ru' | 'en';

export const CONSUMERS = 'consumers';
export const VIP = 'favorites';
export const BLOCK = 'blocked';

export enum AuditOpStatus {
    FAIL = -1,
    OK = 0,
}

export interface AuditPrefix {
    key: string,
    bucket: string,
    prefix: string,
    disk_id: number
}

export interface AuditOperationLink {
    id: number,
    perm: SdnPermissionType,
    type: number,
    alimit: number,
    emails: string[],
    expire: string,
    source: AuditPrefix[]
}

export interface AuditOperationZip {
    source: AuditPrefix[],
    filename?: string,
    archive: AuditPrefix,
}

export interface AuditOperationZipExtract {
    filename?: string,
    archive: AuditPrefix,
    pattern: ['*'],
    destination: AuditPrefix,
}

export interface AuditOperationPerm {
    id: number,
    perm: SdnPermissionType,
    prefix: AuditPrefix,
    grantee?: {
        user?: {
            id: number,
            user: PersonDescription,
            account: AccountListElement,
        } | null,
        group?: {
            id: number,
            name: string,
            system: boolean,
            account: AccountListElement,
            is_accountgroup: boolean
        }
    },
    validity: {
        lower: string | null,
        upper: string | null
    },
}

export interface AuditOperationPermSet {
    id: number,
    permissions: AuditOperationPerm[],
}

export interface AuditOperationFile {
    etag: string,
    size: number,
    speed: number,
    prefix: AuditPrefix,
    status: string,
    versionid: string,
    x_amz_id2: string,
    x_amz_request_id: string
}

export interface AuditOperationObjs {
    size: number,
    source: AuditPrefix[],
    destination: AuditPrefix,
    calculate_size: boolean,
    remove_versions: boolean | null,
    removal_strategy: string | null,
}

export interface AuditOperationRateLimit {
    prefix: AuditPrefix,
    url: string,
    limit: number,
    type: string,
    accesskey: string,
}

export interface AuditOperationGroup {
    id: number,
    name: string,
    account: AccountListElement,
    system: boolean,
    is_accountgroup: boolean,
}

export interface AuditOperationDisk {
    id: number
    bucket: string
    owner: PersonDescription
    expire: string
    size: number
    freespace_p: number
    oversize_p: number
    access_locked: boolean
    qrite_locked: boolean
    low_freespace: boolean
    ratelimits: Record<string, number> // { "daily": 100, "hourly": 30 }
}

export interface AuditOperationKey {
    user: {
        id: number,
        user: PersonDescription,
        account: AccountListElement,
    },
    expire: string,
    accesskey: string,
    ttl: number,
}

export enum AuditOpType {
    UNKNOWN = 0, // _("AUDIT-OP-UNKNOWN", "Неопределена")
    // # Операции с СХД
    SDN_NULL = 10000, // _("AUDIT-SDN-NULL", "Операция с СХД")
    // ## Файлы
    SDN_OBJ_DOWNLOAD = 10010, // _("AUDIT-SDN-OBJ-DOWNLOAD", "Скачивание файла")
    SDN_OBJ_UPLOAD = 10011, // _("AUDIT-SDN-OBJ-UPLOAD", "Загрузка файла")
    SDN_OBJ_DELETE = 10012, // _("AUDIT-SDN-OBJ-DELETE", "Удаление файла")
    // ## Разрешения
    SDN_PERM_SET = 10020, // _("AUDIT-SDN-PERM-SET", "Установка разрешения")
    SDN_PERM_DEL = 10021, // _("AUDIT-SDN-PERM-DEL", "Удаление разрешения")
    SDN_PERM_CHG = 10022, // _("AUDIT-SDN-PERM-CHG", "Изменение разрешения")
    SDN_PERM_ORDER_SET = 10023,  // _("AUDIT-SDN-PERM-ORDER-SET", "Установка разрешения по одобренной заявке")
    // ## Внешние ссылки
    SDN_LINK_ADD = 10030, // _("AUDIT-SDN-LINK-ADD", "Выдана ссылка")
    SDN_LINK_DEL = 10031, // _("AUDIT-SDN-LINK-DEL", "Отозвана ссылка")
    SDN_LINK_CHG = 10032, // _("AUDIT-SDN-LINK-CHG", "Ссылка изменена")
    SDN_LINK_AUTHORIZED = 10033, // _("AUDIT-SDN-LINK-AUTHORIZED", "Заход по ссылке авторизован")                        #!!!
    SDN_LINK_NOT_AUTHORIZED = 10034, // _("AUDIT-SDN-LINK-NOT-AUTHORIZED", "Заход по ссылке не авторизован")                 #!!!
    SDN_LINK_USED = 10035, // _("AUDIT-SDN-LINK-USED", "Заход по ссылке")
    SDN_LINK_USED_LIMITED = 10036, // _("AUDIT-SDN-LINK-USED-LIMITED", "Превышен лимит переходов по ссылке")
    // ## Операции
    SDN_OBJS_RENAME = 10040, // _("AUDIT-SDN-OBJS-RENAME", "Переименование объектов")
    SDN_OBJS_MOVE = 10041, // _("AUDIT-SDN-OBJS-MOVE", "Перемещение объектов")
    SDN_OBJS_COPY = 10042, // _("AUDIT-SDN-OBJS-COPY", "Копирование объектов")
    SDN_OBJS_SAVETODISK = 10043, // _("AUDIT-SDN-OBJS-SAVETODISK", "Сохраниение на диск")
    SDN_OBJS_DELETE = 10044, // _("AUDIT-SDN-OBJS-DELETE", "Удаление объектов")
    // ## Архивы
    SDN_ARCHIVE_ZIP_DOWNLOAD = 10050, // _("AUDIT-SDN-ARCHIVE-ZIP-DOWNLOAD", "Скачивание ZIP архива")
    SDN_ARCHIVE_ZIP_LIST = 10051, // _("AUDIT-SDN-ARCHIVE-ZIP-LIST", "Просмотр ZIP архива")
    SDN_ARCHIVE_ZIP_EXTRACT = 10052, // _("AUDIT-SDN-ARCHIVE-ZIP-EXTRACT", "Распаковка ZIP архива")
    SDN_ARCHIVE_ZIP_CREATE = 10053, // _("AUDIT-SDN-ARCHIVE-ZIP-CREATE", "Создание ZIP архива")
    // # Rate-Limit
    SDN_RATELIMIT_LOCK = 10060, // _("AUDIT-SDN-RATELIMIT-LOCK", "Доступ к файлу заблокирован (rate-limit)")
    SDN_RATELIMIT_UNLOCK = 10061, // _("AUDIT-SDN-RATELIMIT-UNLOCK", "Доступ к файлу разблокирован (rate-limit)")
    // # Группы
    SDN_GROUP_ADD = 10070, // _("AUDIT-SDN-GROUP-ADD", "Создана группа доступа к СХД")
    SDN_GROUP_CHG = 10071, // _("AUDIT-SDN-GROUP-CHG", "Изменена группа доступа к СХД")
    SDN_GROUP_DEL = 10072, // _("AUDIT-SDN-GROUP-DEL", "Удалена группа доступа к СХД")
    // # Диски
    SDN_DISK_ADD = 10080, // _("AUDIT-SDN-DISK-ADD", "Создан диск")
    SDN_DISK_CHG = 10081, // _("AUDIT-SDN-DISK-CHG", "Параметры диска изменены")
    SDN_DISK_DEL = 10082, // _("AUDIT-SDN-DISK-DEL", "Диск удален")
    // ## Сервисные аккаунты (Ключи доступа)
    SDN_SVCACCT_ADD = 10090, // _("AUDIT-SVCACCT-ADD", "Создан ключ доступа")
    SDN_SVCACCT_DEL = 10091, // _("AUDIT-SVCACCT-DEL", "Удален ключ доступа")
    SDN_SVCACCT_INVITE = 10092, // _("AUDIT-SVCACCT-INVITE", "Создано приглашение для создания ключа доступа")
}

export enum AuditOpDealsType {
    // Операции с заявками/разрешениями/договорами
    DEALS_NULL = 20000, // _("AUDIT-DEALS-NULL", "Операция со сделаками")
    // Разрешения
    DEALS_PERMIT_TV_ADD = 20010, //  _("AUDIT-DEALS-PERMIT-TV-ADD", "Создан черновик разрешения на ТВ каналы") # deals.audit.objects.dealsPermitTvAuditObject
    DEALS_PERMIT_TV_CHG = 20011, //  _("AUDIT-DEALS-PERMIT-TV-CHG", "Черновик разрешения на ТВ каналы обновлен") # deals.audit.objects.dealsPermitTvAuditObject
    DEALS_PERMIT_TV_DEL = 20012, //  _("AUDIT-DEALS-PERMIT-TV-DEL", "Черновик разрешения на ТВ каналы удален") # deals.audit.objects.dealsPermitTvAuditObject
    DEALS_PERMIT_TV_ISSUED = 20013, //  _("AUDIT-DEALS-PERMIT-TV-ISSUED", "Выдано разрешение на ТВ каналы") # deals.audit.objects.dealsPermitTvIssuedAuditObject
    DEALS_PERMIT_TV_REVOKED = 20014, //  _("AUDIT-DEALS-PERMIT-TV-REVOKED", "Отозвано разрешение на ТВ каналы") # deals.audit.objects.dealsPermitTvRevokedAuditObject
    DEALS_PERMIT_TV_EXT_ADD = 20015, //  _("AUDIT-DEALS-PERMIT-TV-EXT-ADD", "Создан черновик внешнего разрешения на ТВ каналы") # deals.audit.objects.dealsPermitTvAuditObject
    DEALS_PERMIT_TV_EXT_CHG = 20016, //  _("AUDIT-DEALS-PERMIT-TV-EXT-CHG", "Черновик внешнего разрешения на ТВ каналы обновлен") # deals.audit.objects.dealsPermitTvAuditObject
    DEALS_PERMIT_TV_EXT_DEL = 20017, //  _("AUDIT-DEALS-PERMIT-TV-EXT-DEL", "Черновик внешнего разрешения на ТВ каналы удален") # deals.audit.objects.dealsPermitTvAuditObject
    DEALS_PERMIT_TV_EXT_ISSUED = 20018, //  _("AUDIT-DEALS-PERMIT-TV-EXT-ISSUED", "Выдано внешнее разрешение на ТВ каналы") # deals.audit.objects.dealsPermitTvIssuedAuditObject
    DEALS_PERMIT_TV_EXT_REVOKED = 20019, //  _("AUDIT-DEALS-PERMIT-TV-EXT-REVOKED", "Отозвано внешнее разрешение на ТВ каналы")
}

export enum AuditSubjectType {
    UNDEFINED = 0, // "Неопределен"
    USER = 1, // "Пользователь"
    ACCOUNT = 2, // "Аккаунт"
    USERACCOUNT = 3, // "Пользователь в аккаунте"
    LINKUSER = 4, // "Пользователь по ссылке"
}

export interface Audit {
    id: number,
    optime: string,
    op: AuditOpType,
    opstatus: AuditOpStatus, // int, 0 - ok, 1 - fail (пока не используется)
    ophost: string,
    subject: {
        type: AuditSubjectType,
        subject: {
            id: number,
            email?: string,
            user?: PersonDescription,
            account?: AccountListElement
        }
    },
    operation: AuditOperationLink | AuditOperationZip | AuditOperationZipExtract | AuditOperationKey |
    AuditOperationPerm | AuditOperationPermSet | AuditOperationFile | AuditOperationObjs |
    AuditOperationRateLimit | AuditOperationGroup | AuditOperationDisk
}

export interface AuthState {
    user?: string
    account?: string
    v2fa?: string
    accessToken?: string
    refreshToken?: string
    suUser?: string
    suRefresh?: string
    signKey?: string,
    signSecret?: string,
}

export interface DashboardData {
    storage: {
        total: number,
        used: number,
        free: number
    },
    speed: {
        max: number
    },
    access_requests: {
        all: number,
        opened: number,
        pending: number,
        granted: number,
        granted_downloaded: number,
        rejected: number,
        canceled: number
    },
    access_permissions: {
        all: number, //- всего
        issued: number, //- статус выдано
        revoked: number, //- статус отозвано
        working: number, //- статус в работе
        active: number, //- статус выдано и срок действия не истек
        expired: number //- статус выдано, но срок действия истек
    },
    consumers: {
        total: number
    },
    content: {
        total: number,
        media: number,
        tv: number,
        new: number,
        new_tv: number,
        new_media: number,
    },
    products: {
        new: number,
        total: number,
        limit?: number
    },
    transcoded: {
        finished: number,
        failed: number,
        processed: number
    }
}

export interface FeedbackRequest {
    email: string,
    text: string,
    token?: string
}

export interface LoginRequest {
    username: string,
    password: string,
    token?: string,
}

export interface LoginSuRequest {
    refresh: string
    login_as: string
    suUser: string
    suRefresh: string
}

export interface LogoutSuRequest {
    refresh: string
    username: string
    account?: string
}

export interface OffsetLimit {
    offset: number,
    limit: number,
    [key: string]: string | number
}

export interface Profile {
    phone: string,
    firstname: string,
    lastname: string,
    company: string,
    avatar?: string,
    send_emails: boolean,
    emails?: string[],
    send_sms: boolean,
    phones?: string[]
    send_telegrams: boolean,
    telegrams?: string[],
    comment?: string
}

export interface Source {
    id: number,
    name: string,
    param: {
        stype: SourcesType,
        partner_types: number[],
    }
}

export interface Status {
    code: number,
    msg: string
}

export interface StatusResponse<T> {
    status: Status,
    results: T
}

export interface StatusPagesResponse<T> {
    status: Status,
    results: {
        count: number,
        next: string | null,
        previous: string | null,
        data: T[]
    }
}

export interface SummaryData {
    tools: {
        transcoding_requests: number,
        uploading: number
    },
    access_requests: {
        all: number,
        opened: number,
        pending: number,
        granted: number,
        granted_downloaded: number,
        rejected: number,
        canceled: number
    },
    notifictions: {
        new: number
    },
    sdn: {
        operations_required_actions: string[]
    }
}

export interface StringMap {
    [key: string]: string
}

export interface AccountPreference {
    prefferedSourceTV: boolean | null
    prefferedSourceUnit: boolean | null
    prefferedSourceServices: boolean | null
    sourceSatellite: boolean | null
    sourceMedialodistic: boolean | null
    sourceOther: boolean | null
    hideParticipants: boolean | null
    agr_offer?: boolean | null
    disks: AccountDiskWithOwner[],
}

export interface SpecData {
    have_media_units: boolean,
    have_tv_units: boolean,
    have_media_requests: boolean,
    have_tv_requests: boolean,
    have_tv_permissions: boolean,
    is_tv_rightowner: boolean,
    have_products: boolean,
    have_product_requests: boolean,
}

export interface SystemSetting {
    id: number,
    group: string,
    app: string,
    name: string,
    value: number | boolean | string,
    vtype: 'int' | 'bool' | 'str',
    descr: string,
    editable: boolean,
    edited: boolean,
    updated: string
}

export interface QuotaInfo {
    name: string,
    used: number,
    limit: number | null
}

export interface Quotas {
    mediaunits: QuotaInfo,
    tvunits: QuotaInfo,
    users: QuotaInfo,
    storage: QuotaInfo,
}

export const commonApi = createApi({
    reducerPath: 'commonApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Users', 'UserRequests', 'Content', 'Access', 'Notify', 'NotifyGroup', 'Upload', 'Media', 'Transcode', 'Operation', 'Link', 'Settings'],
    keepUnusedDataFor: 30,
    endpoints: (builder) => ({
        getSummary: builder.query<StatusResponse<SummaryData>, void>({
            query: () => 'be/api/v1/ui/summary/',
            providesTags: (result, error, id) => [{ type: 'Content' }, { type: 'Access' }, { type: 'Notify' }, { type: 'Transcode' }],
        }),
        getDashboard: builder.query<StatusResponse<DashboardData>, void>({
            query: () => 'be/api/ui/dashboard/',
            providesTags: (result, error, id) => [{ type: 'Content' }, { type: 'Access' }, { type: 'Notify' }, { type: 'Transcode' }],
        }),
        getQuotas: builder.query<StatusResponse<Quotas>, void>({
            query: () => 'be/api/v1/ui/quotas/',
            providesTags: (result, error, id) => [{ type: 'Content' }],
        }),
        getSpecs: builder.query<StatusResponse<SpecData>, void | number>({
            query: (acc_id) => `be/api/v1/ui/specs/${acc_id ? ('?id=' + acc_id) : ''}`,
        }),
        getUnits: builder.query<StatusResponse<Pick<DashboardData, 'content'>>, number | undefined>({
            query: (accound_id) => `be/api/v1/ui/units/?id=${accound_id || ''}`,
            providesTags: (result, error, id) => [{ type: 'Content' }],
        }),
        getXls: builder.mutation<string, string>({
            query: (url) => ({
                url: url,
                responseType: 'blob',
                responseDataHandler: async (data: BlobPart) => {
                    const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                    return URL.createObjectURL(blob);
                },
                cache: 'no-cache',
            }),
        }),
        getSources: builder.query<StatusResponse<Source[]>, void>({
            query: () => 'be/api/v1/tv/sources/',
        }),
        getAudit: builder.query<StatusPagesResponse<Audit>, OffsetLimit>({
            query: (params) => `be/api/v1/audit/?${Object.keys(params).filter(k => params[k] || params[k] === 0).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
        }),
        getAuditSdn: builder.query<StatusPagesResponse<Audit>, OffsetLimit>({
            query: (params) => `be/api/v1/sdn/audit/?${Object.keys(params).filter(k => params[k] || params[k] === 0).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
        }),
        login: builder.mutation<AuthResponse, LoginRequest>({
            query: (credentials: LoginRequest) => ({
                url: 'be/token/',
                method: 'POST',
                body: credentials,
            }),
            transformResponse: (response: AuthResponse, meta, arg) => {
                const result = { ...response, username: arg.username }
                return result;
            },
        }),
        accountLogin: builder.mutation<AuthResponse, LogoutSuRequest>({
            query: (credentials: LogoutSuRequest) => ({
                url: 'be/token/refresh/',
                method: 'POST',
                body: credentials,
            }),
            transformResponse: (response: AuthResponse, meta, arg) => {
                const result = { ...response, username: arg.username };
                return result;
            },
            invalidatesTags: (result, error, id) => [{ type: 'Access' }, { type: 'Content' }, { type: 'Users' }, { type: 'Notify' }]
        }),
        suLogin: builder.mutation<AuthResponse, LoginSuRequest>({
            query: (credentials: LoginSuRequest) => ({
                url: 'be/token/su/',
                method: 'POST',
                body: credentials,
            }),
            transformResponse: (response: AuthResponse, meta, arg) => {
                const result = { ...response, username: arg.login_as, suUser: arg.suUser, suRefresh: arg.suRefresh };
                return result;
            },
            invalidatesTags: (result, error, id) => [{ type: 'Access' }, { type: 'Content' }, { type: 'Users' }, { type: 'Notify' }]
        }),
        suLogout: builder.mutation<AuthResponse, LogoutSuRequest>({
            query: (credentials: LogoutSuRequest) => ({
                url: 'be/token/refresh/',
                method: 'POST',
                body: credentials,
            }),
            transformResponse: (response: AuthResponse, meta, arg) => {
                const result = { ...response, refresh: arg.refresh, username: arg.username, suUser: '', suRefresh: '' };
                return result;
            },
            invalidatesTags: (result, error, id) => [{ type: 'Access' }, { type: 'Content' }, { type: 'Users' }, { type: 'Notify' }]
        }),
        sendToSupport: builder.mutation<StatusPagesResponse<{ text: string }>, string>({
            query: (text: string) => ({
                url: 'be/api/support/message/send/',
                method: 'POST',
                body: { text },
            }),
        }),
        sendFeedback: builder.mutation<StatusPagesResponse<{ text: string, email: string }>, FeedbackRequest>({
            query: (data) => ({
                url: 'be/api/feedback/',
                method: 'POST',
                body: data,
            }),
        }),
        // Настройки параметров в медиабазе
        getSystemSettings: builder.query<StatusResponse<SystemSetting[]>, void>({
            query: () => 'be/api/v1/system/settings/',
            providesTags: (result, error, id) => [{ type: 'Settings' }],
        }),
        getSystemSetting: builder.query<StatusResponse<SystemSetting>, number>({
            query: (id) => `be/api/v1/system/settings/${id}/`,
            providesTags: (result, error, id) => [{ type: 'Settings' }],
        }),
        updateSystemSetting: builder.mutation<StatusResponse<SystemSetting>, Pick<SystemSetting, 'id' | 'value'>>({
            query: (data) => ({
                url: `be/api/v1/system/settings/${data.id}/`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Settings' }, { type: 'Settings', id: result?.results.id }]
        }),
    }),
})

const slice = createSlice({
    name: 'auth',
    initialState: {
        user: localStorage.user || undefined,
        account: localStorage.account || undefined,
        v2fa: localStorage.v2fa || undefined,
        accessToken: localStorage.accessToken,
        refreshToken: localStorage.refreshToken,
        suUser: localStorage.suUser,
        suRefresh: localStorage.suRefresh,
        signKey: localStorage.signKey,
        signSecret: localStorage.signSecret,
    },
    reducers: {
        setCredentials: (
            state,
            { payload: { user, account, v2fa, accessToken, refreshToken, suUser, suRefresh, signKey, signSecret } }: PayloadAction<AuthState>
        ) => {
            if (user !== undefined) { state.user = user; localStorage.user = user; }
            if (account !== undefined) { state.account = account; localStorage.account = account; }
            if (v2fa !== undefined) { state.v2fa = v2fa; localStorage.v2fa = v2fa; }
            if (accessToken !== undefined) { state.accessToken = accessToken; localStorage.accessToken = accessToken; }
            if (refreshToken !== undefined) { state.refreshToken = refreshToken; localStorage.refreshToken = refreshToken; }
            if (suUser !== undefined) { state.suUser = suUser; localStorage.suUser = suUser; }
            if (suRefresh !== undefined) { state.suRefresh = suRefresh; localStorage.suRefresh = suRefresh; }
            if (signKey !== undefined) { state.signKey = signKey; localStorage.signKey = signKey; }
            if (signSecret !== undefined) { state.signSecret = signSecret; localStorage.signSecret = signSecret; }

            // if (state.user !== undefined) localStorage.user = state.user;
            // if (state.account !== undefined) localStorage.account = state.account;
            // if (state.accessToken !== undefined) localStorage.accessToken = state.accessToken;
            // if (state.refreshToken !== undefined) localStorage.refreshToken = state.refreshToken;
            // if (state.suUser !== undefined) localStorage.suUser = state.suUser;
            // if (state.suRefresh !== undefined) localStorage.suRefresh = state.suRefresh;
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            commonApi.endpoints.login.matchFulfilled,
            (state, { payload }) => {
                const jwt = payload.access.split('.')[1];
                const jwtDecoded = JSON.parse(atob(jwt));

                state.accessToken = payload.access
                state.refreshToken = payload.refresh
                state.user = payload.username
                state.account = jwtDecoded.accound_id
                state.v2fa = jwtDecoded.v2fa
                state.suUser = ''
                state.suRefresh = ''
                state.signKey = ''
                state.signSecret = ''

                localStorage.accessToken = payload.access
                localStorage.refreshToken = payload.refresh
                localStorage.user = payload.username
                localStorage.account = jwtDecoded.accound_id || ''
                localStorage.v2fa = jwtDecoded.v2fa || false
                localStorage.suUser = ''
                localStorage.suRefresh = ''
                localStorage.signKey = ''
                localStorage.signSecret = ''
            },
        ).addMatcher(
            commonApi.endpoints.accountLogin.matchFulfilled,
            (state, { payload }) => {
                const jwt = payload.access.split('.')[1];
                const jwtDecoded = JSON.parse(atob(jwt));

                const jwtSigning = payload.signing?.split('.')[1];
                const jwtSigningDecoded = jwtSigning ? JSON.parse(atob(jwtSigning)) : {};

                state.accessToken = payload.access
                if (payload.refresh) state.refreshToken = payload.refresh
                state.user = payload.username
                state.account = jwtDecoded.accound_id
                state.v2fa = jwtDecoded.v2fa
                state.signKey = jwtSigningDecoded?.key
                state.signSecret = jwtSigningDecoded?.secret

                localStorage.accessToken = payload.access
                if (payload.refresh) localStorage.refreshToken = payload.refresh
                localStorage.user = payload.username
                localStorage.account = jwtDecoded.accound_id || ''
                localStorage.v2fa = jwtDecoded.v2fa
                localStorage.signKey = jwtSigningDecoded?.key
                localStorage.signSecret = jwtSigningDecoded?.secret
            },
        ).addMatcher(
            commonApi.endpoints.suLogin.matchFulfilled,
            (state, { payload }) => {
                const jwt = payload.access.split('.')[1];
                const jwtDecoded = JSON.parse(atob(jwt));

                const jwtSigning = payload.signing?.split('.')[1];
                const jwtSigningDecoded = jwtSigning ? JSON.parse(atob(jwtSigning)) : {};

                state.accessToken = payload.access
                state.refreshToken = payload.refresh
                state.user = payload.username
                state.account = jwtDecoded.accound_id || ''
                state.v2fa = jwtDecoded.v2fa
                state.suUser = payload.suUser
                state.suRefresh = payload.suRefresh
                state.signKey = jwtSigningDecoded?.key
                state.signSecret = jwtSigningDecoded?.secret

                localStorage.accessToken = payload.access
                localStorage.refreshToken = payload.refresh
                localStorage.user = payload.username
                localStorage.account = jwtDecoded.accound_id || ''
                localStorage.v2fa = jwtDecoded.v2fa
                localStorage.suUser = payload.suUser
                localStorage.suRefresh = payload.suRefresh
                localStorage.signKey = jwtSigningDecoded?.key
                localStorage.signSecret = jwtSigningDecoded?.secret
            },
        ).addMatcher(
            commonApi.endpoints.login.matchRejected,
            (state, { payload }) => {
                state.accessToken = ''
                state.refreshToken = ''
                state.user = ''
                state.account = ''
                state.v2fa = ''
                state.suUser = ''
                state.suRefresh = ''
                state.signKey = ''
                state.signSecret = ''

                delete localStorage.accessToken;
                delete localStorage.refreshToken;
                delete localStorage.user;
                delete localStorage.account;
                delete localStorage.v2fa;
                delete localStorage.suUser;
                delete localStorage.suRefresh;
                delete localStorage.signKey;
                delete localStorage.signSecret;
            }
        ).addMatcher(
            commonApi.endpoints.suLogout.matchFulfilled,
            (state, { payload }) => {
                const jwt = payload.access.split('.')[1];
                const jwtDecoded = JSON.parse(atob(jwt));

                state.accessToken = payload.access
                state.refreshToken = payload.refresh
                state.user = payload.username
                state.account = jwtDecoded.accound_id
                state.suUser = ''
                state.suRefresh = ''
                state.signKey = ''
                state.signSecret = ''

                localStorage.accessToken = payload.access
                localStorage.refreshToken = payload.refresh
                localStorage.user = payload.username
                localStorage.account = jwtDecoded.accound_id
                localStorage.suUser = ''
                localStorage.suRefresh = ''
                localStorage.signKey = ''
                localStorage.signSecret = ''
            },
        )
    },
});

const roleSlice = createSlice({
    name: 'role',
    initialState: EMPTY_ROLE,
    reducers: {
        setRole: (
            state,
            { payload: { id, label: name, name: displayname, ts } }: PayloadAction<Role>
        ) => {
            state.id = id
            state.label = name
            state.name = displayname
            state.ts = ts
        }
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            userApiV1.endpoints.getProfile.matchFulfilled,
            (state, { payload }) => {
                const ts = payload.ts;
                if (!state.ts || !ts || state.ts < ts) {
                    const tmp = payload.results.roles.at(0) || EMPTY_ROLE
                    state.id = tmp.id;
                    state.label = tmp.label;
                    state.name = tmp.name;
                    state.ts = ts
                }
            }
        ).addMatcher(
            commonApi.endpoints.login.matchRejected,
            (state, { payload }) => {
                state = EMPTY_ROLE
            }
        )
    },
});

const otpInitialState: { otp_enabled?: boolean, otp_configured?: boolean } = { otp_enabled: undefined, otp_configured: undefined };

const otpSlice = createSlice({
    name: 'otp',
    initialState: otpInitialState,
    reducers: {
        setRole: (
            state,
            { payload: { otp_enabled, otp_configured } }: PayloadAction<{ otp_enabled?: boolean, otp_configured?: boolean }>
        ) => {
            state.otp_enabled = otp_enabled
            state.otp_configured = otp_configured
        }
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            userApiV1.endpoints.getProfile.matchFulfilled,
            (state, { payload }) => {
                state.otp_enabled = payload.results.otp_enabled;
                state.otp_configured = payload.results.otp_configured
            }
        )
    },
});

const accSliceInitialState: AccountPreference = { prefferedSourceTV: null, prefferedSourceUnit: null, prefferedSourceServices: null, sourceSatellite: null, sourceMedialodistic: null, sourceOther: null, hideParticipants: null, agr_offer: false, disks: [] };

const accSlice = createSlice({
    name: 'acc',
    initialState: accSliceInitialState,
    reducers: {
        setAcc: (
            state,
            { payload: { prefferedSourceTV, prefferedSourceUnit, prefferedSourceServices, sourceSatellite, sourceMedialodistic, sourceOther, hideParticipants, agr_offer, disks } }: PayloadAction<AccountPreference>
        ) => {
            state.prefferedSourceTV = prefferedSourceTV;
            state.prefferedSourceUnit = prefferedSourceUnit;
            state.prefferedSourceServices = prefferedSourceServices;
            state.sourceSatellite = sourceSatellite;
            state.sourceMedialodistic = sourceMedialodistic;
            state.sourceOther = sourceOther;
            state.hideParticipants = hideParticipants;
            state.agr_offer = agr_offer;
            state.disks = disks;
        }
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            userApiV1.endpoints.getCurrentAccount.matchFulfilled,
            (state, { payload }) => {
                const tmp = payload.results.additional;
                if (tmp) {
                    state.prefferedSourceTV = tmp.prefferedSourceTV
                    state.prefferedSourceUnit = tmp.prefferedSourceUnit
                    state.prefferedSourceServices = tmp.prefferedSourceServices
                    state.sourceSatellite = tmp.sourceSatellite
                    state.sourceMedialodistic = tmp.sourceMedialodistic
                    state.sourceOther = tmp.sourceOther
                    state.hideParticipants = tmp.hideParticipants
                    state.agr_offer = payload.results.agr_offer
                    state.disks = payload.results.disks
                }
            }
        ).addMatcher(
            commonApi.endpoints.login.matchRejected,
            (state, { payload }) => {
                state.prefferedSourceTV = null
                state.prefferedSourceUnit = null
                state.prefferedSourceServices = null
                state.sourceSatellite = null
                state.sourceMedialodistic = null
                state.sourceOther = null
                state.hideParticipants = null
                state.agr_offer = false
                state.disks = []
            }
        )
    },
});

export const { setCredentials } = slice.actions
export const { setRole } = roleSlice.actions
export const { reducer: roleReducer } = roleSlice
export const { setAcc } = accSlice.actions
export const { reducer: accReducer } = accSlice
export const { reducer: otpReduser } = otpSlice

export default slice.reducer;

// Export hooks for usage in functional components
export const {
    useAccountLoginMutation,
    useGetDashboardQuery,
    useGetSourcesQuery,
    useGetSpecsQuery,
    useGetSummaryQuery,
    useGetUnitsQuery,
    useGetXlsMutation,
    useLazyGetDashboardQuery,
    useLazyGetSourcesQuery,
    useLazyGetSpecsQuery,
    useLazyGetSummaryQuery,
    useLazyGetUnitsQuery,
    useLoginMutation,
    useSendFeedbackMutation,
    useSendToSupportMutation,
    useSuLoginMutation,
    useSuLogoutMutation,
    useGetQuotasQuery,
    useLazyGetQuotasQuery,
    useGetSystemSettingQuery,
    useGetSystemSettingsQuery,
    useLazyGetSystemSettingQuery,
    useLazyGetSystemSettingsQuery,
    useUpdateSystemSettingMutation,
    useGetAuditQuery,
    useLazyGetAuditQuery,
    useGetAuditSdnQuery,
    useLazyGetAuditSdnQuery,
} = commonApi;
